import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Hammer, Link2, X } from 'lucide-react';
import { useApiClient } from '@core/api/useApiClient';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { ACTION_FLAGS, useMe } from '@features/user-profile/hooks/useMe';
import { useSetActionFlag } from '@features/user-profile/hooks/useSetActionFlag';
import { useAccountConnection } from '@features/accounts/hooks/useAccountConnection';
import { useConnectDialogStore } from '@features/accounts/state/connectDialogStore';
import { readAccountLogin } from '@features/accounts/lib/accountLogins';
import { routes } from '@config/routes';
import { CqgConnectorDialog } from './CqgConnectorDialog';
import { BRAND_GRADIENT } from '@shared/styles/brand';

// BE serialises the enum camelCase (matches CqgStateBadge).
type CqgState = 'disconnected' | 'connecting' | 'live' | 'error';

interface CqgStatus {
  state: CqgState;
  accounts: string[];
  lastError: string | null;
}

function normalise(state: string | undefined | null): CqgState {
  const key = (state ?? 'disconnected').toLowerCase();
  return (['disconnected', 'connecting', 'live', 'error'].includes(key) ? key : 'disconnected') as CqgState;
}

/**
 * FIRST-TIME onboarding banner shown directly under the top bar while CQG is NOT connected. It
 * mirrors {@link CqgStateBadge}'s state source — the shared `['cqg','status']` query plus the
 * realtime `cqg.state` push. Clicking "Connect CQG" opens the connector dialog (which runs the full
 * account-sync workflow server-side — spec step 5).
 *
 * First-time gating (BE-backed, per-user — not per browser): the bar hides for good once the user
 * has EVER connected CQG (`cqg_connected`, set server-side on logon success) OR has dismissed it
 * (`cqg_connect_prompt_seen` via the ✕, recorded through POST /me/action-flags). Both arrive on
 * /me `actionFlags`. While /me is still loading the bar stays hidden — it flashes in only for
 * genuinely-unflagged users rather than flashing for everyone.
 *
 * Figma: a 44px mint bar (`rgba(102,255,200,0.2)`) with a "Start here" prompt (`--text`, Inter 14/24)
 * and a brand-gradient (`#009AC8 → #1BB980`) "Connect CQG" pill (white, Inter 600 13/16, link icon).
 */
export function CqgConnectBanner() {
  const location = useLocation();
  const api = useApiClient();
  const realtime = useRealtime();
  const me = useMe();
  const setFlag = useSetActionFlag();
  const [pushed, setPushed] = useState<CqgState | null>(null);
  const [open, setOpen] = useState(false);

  const status = useQuery({
    queryKey: ['cqg', 'status'],
    queryFn:  () => api.get<CqgStatus>('/api/v1/cqg/status'),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    return realtime.subscribe('cqg.state', (payload) => setPushed(normalise(payload.state)));
  }, [realtime]);

  const state = pushed ?? normalise(status.data?.state);

  // First-time gate: hidden until /me resolves, then hidden for good once either flag is set —
  // OR once there is ANY durable evidence the account has connected before (2026-07-08 bug: the
  // bar re-appeared for already-synced users because `cqg_connected` reached the FE only via the
  // connected-popup's narrow ['me'] invalidation; a stale /me kept firstTime=true while the raw
  // status was between sessions). CQG-reported accounts on the status payload persist across
  // sessions server-side, so a non-empty list means "has synced" regardless of flag freshness.
  const flags = me.data?.actionFlags;
  const hasCqgAccounts = (status.data?.accounts?.length ?? 0) > 0;
  const firstTime = !!flags
    && !flags.includes(ACTION_FLAGS.cqgConnected)
    && !flags.includes(ACTION_FLAGS.cqgConnectPromptSeen)
    && !hasCqgAccounts;

  // "Selected but not connected" nudge (2026-07-18): when the user selects a CQG account whose
  // session isn't live for it, show the same bar so they can connect THAT account (opens the dialog
  // preselected to its Demo/Live tab + remembered username). Distinct from the first-time onboarding
  // gate above — this fires for an already-synced user viewing an offline account. It cannot be closed
  // (owner, 2026-10-20: the local dismiss came back on every page change anyway, "and we want user to
  // connect"); only the first-time bar keeps its ✕, so a user who never uses CQG can still drop it.
  const { accounts, selectedName, selected } = useAccountConnection();
  const store = useConnectDialogStore();
  const selectedInfo = accounts.data?.find(a => a.name === selectedName);
  const showOfflineBar = selectedInfo?.provider === 'cqg' && selected.display === 'offline';

  // Only show the first-time BAR when the user can act on it: first time, not connected and not
  // mid-connect (while `connecting`/`live` the top-bar control already communicates state). But the
  // dialog must stay mounted whenever it's `open` — a connect started FROM the banner flips state to
  // `connecting`, and if the whole component unmounted here it would tear down the import-progress
  // dialog mid-import. So only render nothing when there's no bar AND no open dialog.
  const showFirstTimeBar = firstTime && state !== 'live' && state !== 'connecting';
  // Suppressed on the AI Backtester (2026-07-24 request): it runs on historical S3 bars, not a
  // live CQG session, so nudging to connect there is irrelevant noise. The dialog itself stays
  // mounted below regardless of route — see the comment above about not tearing down an in-progress
  // import if the user navigates here mid-connect.
  const onBacktestingRoute = location.pathname.startsWith(routes.backtesting);
  // Prefer the account-specific nudge when a specific offline account is selected.
  const mode: 'offline' | 'firstTime' | null = onBacktestingRoute
    ? null
    : showOfflineBar ? 'offline' : showFirstTimeBar ? 'firstTime' : null;
  if (!mode && !open) return null;

  const connectOfflineSelected = () => {
    if (!selectedInfo) return;
    const remembered = readAccountLogin(selectedInfo.name);
    store.openCqg({
      environment: selectedInfo.environment ?? remembered?.environment ?? undefined,
      username: remembered?.username ?? undefined,
    });
  };

  return (
    <>
      {mode && (
        <div
          className="flex h-11 shrink-0 items-center gap-3 px-4"
          style={{ background: 'rgba(102, 255, 200, 0.2)' }}
        >
          <Hammer className="h-4 w-4 shrink-0" style={{ color: 'var(--text)' }} aria-hidden />
          <p className="text-sm leading-6" style={{ color: 'var(--text)' }}>
            {mode === 'offline'
              ? <><span className="font-semibold">{selectedInfo?.displayName || selectedInfo?.name}</span> isn&apos;t connected. Connect CQG to sync it live.</>
              : <><span className="font-semibold">Start here:</span> Connect your CQG account to sync your trades automatically.</>}
          </p>
          <button
            type="button"
            onClick={() => (mode === 'offline' ? connectOfflineSelected() : setOpen(true))}
            className="inline-flex h-8 shrink-0 items-center justify-center gap-2 rounded-lg px-2 text-[13px] font-semibold leading-4 text-white"
            style={{ background: BRAND_GRADIENT }}
          >
            <Link2 className="h-3 w-3" aria-hidden />
            Connect CQG
          </button>
          {/* Dismiss, first-time bar only: records cqg_connect_prompt_seen (BE-backed, never returns).
              The offline nudge has none (2026-10-20). */}
          {mode === 'firstTime' && (
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setFlag.mutate(ACTION_FLAGS.cqgConnectPromptSeen)}
              className="ml-auto rounded p-1 transition-opacity hover:opacity-70"
              style={{ color: 'var(--text)' }}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      )}
      <CqgConnectorDialog
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
